import { html, LitElement, unsafeCSS } from "lit-element"
import { classMap } from "lit-html/directives/class-map"
import commonStyle from "../common.css"
import style from "./treeview.css"
import nodeStyle from "../tree-node/tree-node.css"

import TreeNode from "../tree-node"

export default class extends LitElement {
	static styles = [unsafeCSS(style), unsafeCSS(commonStyle), unsafeCSS(nodeStyle)]
	static get properties () {
		return {
			// アイコン表示フラグ
			showIcon: {
				type: Boolean,
				attribute: "show-icon",
				converter: {
					fromAttribute: (value, type) => {
						return value != null && value !== "false" // 未設定あるいは"false"の時false
					}
				}
			},
			// 展開アイコン表示フラグ
			showExpandIcon: { type: Boolean, attribute: "show-expand-icon" },
			// チェックボックス表示フラグ
			showCheckbox: {
				type: Boolean,
				attribute: "show-checkbox",
				converter: {
					fromAttribute: (value, type) => {
						return value != null && value !== "false" // 未設定あるいは"false"の時false
					}
				}
			}
		}
	}

	#treeNodes = []
	#currentNode = null

	constructor () {
		super()
		this.showIcon = true
		this.showExpandIcon = false
		this.showCheckbox = false
	}

	render () {
		return html`
			<div class="treeview-wrapper">
				${this.#treeNodes.map(treeNode => this.#renderNode(treeNode))}
			</div>
		`
	}

	/** 描画する */
	#renderNode (treeNode, layer = 0) {
		treeNode.layer = layer
		++layer

		return html`
		${treeNode.render(this.showIcon, this.showExpandIcon)}
		<div class=${this.#getChildNodesClass(treeNode)}>
			${treeNode.childNodes.map(child => this.#renderNode(child, layer))}
		</div>
		`
	}

	/** ChildNodes展開クラス設定 */
	#getChildNodesClass (treeNode) {
		return classMap({
			"is-expanded": !treeNode.isExpanded
		})
	}

	get currentNode () {
		return this.#currentNode
	}

	/**
	 * 初期表示／更新処理
	 */
	initTree (treeNodes) {
		return this.updateComplete.then(() => {
			this.deleteAllNode()

			treeNodes.forEach(treeNode => {
				this.#treeNodes.push(this.createNode(treeNode))
			})
		}).then(() => {
			return this.requestUpdate()
		})
	}

	get rootNode () {
		return this.#treeNodes.length > 0 ? this.#treeNodes[0] : null
	}

	get nodeCount () {
		return this.#treeNodes.length
	}

	/**
	 * ノード追加処理
	 */
	addNode (addNode) {
		// カレントノード検索
		const selectedNode = this.searchCurrentNode()

		if (selectedNode.iconType !== "1") {
			return
		}

		// カレントノードに追加
		selectedNode.addNode(this.createNode(addNode))

		// 強制的に展開＆選択
		selectedNode.expand()
		const last = selectedNode.childNodes.slice(-1)[0]
		last.selectNode()

		this.requestUpdate()
	}

	/**
	 * 全ノード削除処理
	 */
	deleteAllNode () {
		this.#treeNodes = []
	}

	/**
	 * ノード削除処理
	 */
	deleteNode () {
		// カレントノード検索
		const deleteNode = this.searchCurrentNode()
		deleteNode.deleteNode()

		this.requestUpdate()
	}

	/**
	 * カレントノード検索(選択中ノードを返す)
 	 * @return {TreeNode}
	 */
	searchCurrentNode () {
		let found = null

		// 選択行検索
		for (let i = 0; this.#treeNodes.length > i; i++) {
			const _node = this.#treeNodes[i]
			if (_node.isSelected) {
				found = _node
				break
			}
			found = this.#searchChildNode(_node.childNodes)
			if (found != null) { break }
		}

		return found
	}

	searchCheckedNodes () {
		const searchCheckedNodesRecursively = (nodes) => {
			return nodes.reduce((found, node) => {
				if (node.checked) {
					found.push(node)
				}
				if (node.childNodes) {
					found = found.concat(searchCheckedNodesRecursively(node.childNodes))
				}
				return found
			}, [])
		}
		return searchCheckedNodesRecursively(this.#treeNodes)
	}

	/**
	 * 子ノード探索
	 */
	#searchChildNode (_nodes) {
		let found = null

		for (let i = 0; _nodes.length > i; i++) {
			const _node = _nodes[i]
			if (_node.isSelected) {
				found = _node
				break
			}
			found = this.#searchChildNode(_node.childNodes)
			if (found != null) { break }
		}
		return found
	}

	/**
	 * 検索処理
	 * @return {Array[TreeNode]}
	 */
	search (predictCallBack, treeNodes = this.#treeNodes) {
		let foundList = []
		treeNodes.forEach(treeNode => {
			if (predictCallBack(treeNode.text, treeNode.userData)) {
				foundList.push(treeNode)
			}
			if (treeNode.childNodes.length > 0) {
				foundList = foundList.concat(this.search(predictCallBack, treeNode.childNodes))
			}
		})
		return foundList
	}

	/**
	 * ツリーノード作成処理
	 */
	createNode ({ text, subText, iconType, icon, childNodes, userData }, parentNode = "") {
		const treeNode = new TreeNode(this)
		treeNode.text = text
		treeNode.subText = subText
		treeNode.iconType = iconType
		treeNode.icon = (iconType === "1") ? treeNode.ICON_FOLDER : icon
		treeNode.parentNode = parentNode
		treeNode.childNodes = childNodes.map(child => this.createNode(child, treeNode))
		treeNode.userData = userData

		// ノード選択時処理
		treeNode.addEventListener("node-selected", () => {
			this.#treeNodes.forEach(node => {
				// 全選択解除処理
				allNodeDeselection(node)
			})
			treeNode.isSelected = true
			this.#currentNode = treeNode

			this.dispatchEvent(new CustomEvent("node-selected", {
				detail: {
					node: treeNode
				}
			}))

			this.requestUpdate()
		})

		// // TODO
		// // ノード展開時処理
		// treeNode.addEventListener("node-expand", () => {
		// 	this.requestUpdate()
		// })

		// // TODO
		// // 削除イベント
		// treeNode.addEventListener("node-delete", () => {
		// 	this.requestUpdate()
		// })

		/**
		 * ノード選択解除処理
		 */
		function allNodeDeselection (node) {
			node.isSelected = false
			node.childNodes.forEach(_cildren => {
				allNodeDeselection(_cildren)
			})
		}

		return treeNode
	}
}
